import React from "react";

import { doNothing, getClosureFor } from "@kazoohr/helpers";

import { Flex } from "../Flex";
import { Spacer } from "../Grid/Spacer";
import { Text } from "../Text";

import styles from "./RatingBar.scss";
import { RatingButton } from "./RatingButton";

export interface RatingBarProps {
  /**
   * Scale that will initiate the rating count
   */
  scaleStart?: number;
  /**
   * Scale that will end the rating count
   */
  scaleEnd?: number;
  /**
   * RatingBar selected value
   */
  rating?: number;
  /**
   * Text to display beneath the start scale
   */
  startLabel?: string;
  /**
   * Text to display beneath the end scale
   */
  endLabel?: string;
  /**
   * Function to call when the rating changes
   */
  onChange: (newRating: number | undefined) => void;
  /**
   * Displays an error message
   */
  error?: string;
  /**
   * Disables onClick action and can't set new values
   */
  disabled?: boolean;
}

interface HelperTextProps {
  content?: string;
  classNameString: string;
}

function HelperText({
  content,
  classNameString,
}: HelperTextProps): JSX.Element | null {
  if (!content) {
    return null;
  }

  return (
    <Text
      size="paragraphXsmall"
      paragraph
      className={styles[classNameString]}
      data-testid="error-message"
    >
      {content}
    </Text>
  );
}

export function RatingBar({
  rating: ratingProp,
  endLabel = "",
  onChange,
  scaleEnd = 5,
  scaleStart = 1,
  startLabel = "",
  error = "",
  disabled = false,
}: RatingBarProps): JSX.Element | null {
  const [rating, setRating] = React.useState<number | undefined>(ratingProp);
  const isFirstRender = React.useRef(true);
  const onChangeRef = React.useRef(onChange);

  const onHandleClick = (value: number): void => {
    if (value === rating) {
      setRating(undefined);
      return;
    }
    setRating(value);
  };

  const calculateTotalOfOptions = (): number[] => {
    const options: number[] = [];
    for (let i = scaleStart; i <= scaleEnd; i++) {
      options.push(i);
    }
    return options;
  };

  const options = calculateTotalOfOptions();

  React.useEffect(() => {
    // Don't call onChange on the first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChangeRef.current(rating);
  }, [rating]);

  if (scaleStart > scaleEnd) {
    return null;
  }

  const hasError = error ? error.trim() !== "" : false;
  return (
    <div className={styles.container} data-testid="rating-bar">
      <HelperText content={error} classNameString="group-helper-text--error" />
      <Flex justifyContent="space-between">
        {options.map((value) => (
          <RatingButton
            key={value}
            value={value}
            currentRating={rating}
            scaleStart={scaleStart}
            scaleEnd={scaleEnd}
            onClick={disabled ? doNothing : getClosureFor(onHandleClick, value)}
            hasError={hasError}
            disabled={disabled}
          />
        ))}
      </Flex>
      {(startLabel || endLabel) && (
        <>
          <Spacer />
          <Flex justifyContent="space-between">
            <div className={styles.label} data-testid="start-label">
              {startLabel}
            </div>
            <div className={styles.label} data-testid="end-label">
              {endLabel}
            </div>
          </Flex>
        </>
      )}
    </div>
  );
}
